import { poll } from '@blueberry/utils/common'
import { Flow, SignupData } from '../types'
import { getLogger } from '@blueberry/observability'

const logger = getLogger('signup/analytics/gtag')

declare global {
  interface Window {
    gtag: (command: string, event: string, payload?: unknown) => void
  }
}

const checkoutProgress = {
  name: 'checkout_progress',
  category: 'signup',
  d2c: {
    page_1: 'Signup Page One',
    page_2: 'Signup Page Two',
    page_3: 'Signup Page Three',
    success: 'Signup Success',
  },
  insurance: {
    page_1: 'Signup Insurance Contact Page',
    success: 'Insurance Signup Success',
  },
}

const pageEventLabel = {
  page_1: 'signup_page_1',
  page_2: 'signup_page_2',
  page_3: 'signup_page_3',
}

const conversionID = {
  page_1: 'AW-816975002/XGIACPGc9n4QmpnIhQM',
  page_2: 'AW-816975002/ldlKCMjk24ABEJqZyIUD',
  page_3: 'AW-816975002/kTTGCOTeh6sZEJqZyIUD',
  success: 'AW-816975002/4__hCKXQ-X4QmpnIhQM',
}

export const gtag = {
  async trackStep(flow: Flow, step: number, data: SignupData) {
    const gtag = await poll(() => window.gtag)
    if (!gtag) {
      logger.error('Retries exhausted waiting for gtag to initialize')
      return
    }

    try {
      if (flow === 'd2c') {
        switch (step) {
          case 1:
            gtag('event', 'checkout_progress', {
              event_label: checkoutProgress.d2c.page_1,
              event_category: checkoutProgress.category,
            })
            gtag('event', pageEventLabel.page_1)
            gtag('event', 'conversion', {
              send_to: conversionID.page_1,
            })
            break
          case 2:
            // We must set user_data before sending the gtag event to ensure that user is
            // associated with conversion events
            gtag('set', 'user_data', {
              email: data.account?.email || '',
            })

            gtag('event', 'checkout_progress', {
              event_label: checkoutProgress.d2c.page_2,
              event_category: checkoutProgress.category,
            })

            gtag('event', pageEventLabel.page_2)
            gtag('event', 'conversion', {
              send_to: conversionID.page_2,
            })
            break
          case 3:
            gtag('event', 'checkout_progress', {
              event_label: checkoutProgress.d2c.page_3,
              event_category: checkoutProgress.category,
            })

            gtag('event', pageEventLabel.page_3)
            gtag('event', 'conversion', {
              send_to: conversionID.page_3,
            })
            break
        }
      }

      if (['insurance_child', 'insurance_parent'].includes(flow)) {
        switch (step) {
          case 1:
            gtag('event', 'checkout_progress', {
              event_label: checkoutProgress.insurance.page_1,
              event_category: checkoutProgress.category,
            })
            break
          case 2:
            gtag('set', 'user_data', {
              email: data.account?.email || '',
            })
            break
          case 3:
            // currently no events for this step
            break
        }
      }
    } catch (error) {
      logger.error('Error tracking gtag step event:', error)
      return
    }
  },
  async trackSuccess(flow: Flow) {
    const gtag = await poll(() => window.gtag)
    if (!gtag) {
      logger.error('Retries exhausted waiting for gtag to initialize')
      return
    }

    if (flow === 'd2c') {
      try {
        await Promise.all([
          new Promise((resolve) => {
            gtag('event', 'checkout_progress', {
              event_label: checkoutProgress.d2c.success,
              event_category: checkoutProgress.category,
              event_callback: () => resolve(true),
            })
          }),
          new Promise((resolve) => {
            gtag('event', 'conversion', {
              send_to: conversionID.success,
              event_callback: () => resolve(true),
            })
          }),
        ])
      } catch (error) {
        logger.error('Error tracking gtag success event:', error)
        return
      }
    }

    if (['insurance_child', 'insurance_parent'].includes(flow)) {
      try {
        gtag('event', 'checkout_progress', {
          event_label: checkoutProgress.insurance.success,
          event_category: checkoutProgress.category,
        })
      } catch (error) {
        logger.error('Error tracking gtag success event:', error)
        return
      }
    }
  },
}
